import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import type { SidebarItem } from './types';

interface NavItemCollapsedProps {
  item: SidebarItem;
  onClick?: () => void;
}

export const NavItemCollapsed = ({ item, onClick }: NavItemCollapsedProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = item.path ? location.pathname === item.path : false;
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;

  // Items with children navigate to their path on click
  const handleClick = () => {
    if (item.path) {
      navigate({ to: item.path });
    }
    onClick?.();
  };

  const content = (
    <Tooltip title={item.label} placement="right" arrow>
      <Box
        onClick={hasChildren ? handleClick : onClick}
        sx={{
          gap: 0.4,
          p: '3px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: theme => (isActive ? theme.palette.grey[900] : theme.palette.grey[500]),
          borderRadius: 1,
          '&:hover': {
            bgcolor: theme => theme.palette.grey[200],
          },
        }}
      >
        <Icon width={24} height={24} size={24} />
        <Typography
          variant="caption"
          sx={{ fontSize: '9px', lineHeight: 1.3, mt: 0.25, fontWeight: isActive ? 700 : 400 }}
        >
          {item.label}
        </Typography>
      </Box>
    </Tooltip>
  );

  // Items with onClick handler
  if (item.onClick && !hasChildren) {
    return (
      <Box onClick={item.onClick} sx={{ cursor: 'pointer', width: '100%', textAlign: 'center' }}>
        {content}
      </Box>
    );
  }

  // Items with external URL open in a new tab
  if (item.externalUrl && !hasChildren) {
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit', width: '100%', textAlign: 'center' }}
      >
        {content}
      </a>
    );
  }

  // Items with path but NO children use Link for navigation
  if (item.path && !hasChildren) {
    return (
      <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit', width: '100%', textAlign: 'center' }}>
        {content}
      </Link>
    );
  }

  // Items with children (with or without path) handle click via handleClick
  return content;
};
